
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { motion } from 'framer-motion';
import { Shield, Globe, Lock, Mail } from 'lucide-react';

interface AboutLandingProps {
    onLogin: () => void;
    onNavigate: (page: 'landing' | 'docs' | 'solutions' | 'card-landing' | 'about-landing' | 'legal') => void;
    onNavigateToDocs: (section?: string) => void;
}

export const AboutLanding: React.FC<AboutLandingProps> = ({ onLogin, onNavigate, onNavigateToDocs }) => {
    return (
        <div className="bg-white dark:bg-[#0A0B0F] min-h-screen font-sans text-[#0A0B0F] dark:text-white selection:bg-nobel-gold selection:text-black">
            <Header onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={(p) => onNavigate(p)} />
            
            <main>
                {/* Hero Section */}
                <section className="pt-32 pb-24 relative overflow-hidden bg-stone-50 dark:bg-[#0A0B0F]">
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-stone-300 dark:via-white/20 to-transparent opacity-50"></div>
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-stone-200 dark:border-white/10 bg-white dark:bg-white/5 text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-8 shadow-sm">
                                    <Shield size={12} className="text-nobel-gold" /> The Sovereign Mission
                                </div>
                                <h1 className="font-serif text-5xl md:text-7xl font-bold mb-8 leading-tight tracking-tight text-[#0A0B0F] dark:text-white">
                                    We build for the <br />
                                    <span className="italic text-stone-400 dark:text-stone-500 font-light text-6xl md:text-8xl">Ghost Network.</span>
                                </h1>
                                <p className="text-xl text-stone-600 dark:text-stone-400 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
                                    Financial privacy isn't a feature—it's a human right. Ghost Protocol is an independent engineering lab dedicated to reclaiming individual sovereignty through high-performance on-chain infrastructure.
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Values Grid */}
                <section className="py-24 bg-white dark:bg-[#0A0B0F] border-y border-stone-100 dark:border-white/5">
                    <div className="container mx-auto px-6">
                        <div className="grid md:grid-cols-3 gap-12 text-[#0A0B0F] dark:text-white">
                            <div className="space-y-6">
                                <div className="w-12 h-12 bg-stone-900 dark:bg-white rounded-xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] shadow-lg">
                                    <Lock size={24} />
                                </div>
                                <h3 className="font-serif text-2xl font-bold">Absolute Privacy</h3>
                                <p className="text-stone-500 text-sm leading-relaxed">
                                    Our stack utilizes zero-knowledge metadata validation and single-use stealth addresses. We ensure your financial activity remains invisible to surveillance capitalism.
                                </p>
                            </div>
                            <div className="space-y-6">
                                <div className="w-12 h-12 bg-stone-900 dark:bg-white rounded-xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] shadow-lg">
                                    <Globe size={24} />
                                </div>
                                <h3 className="font-serif text-2xl font-bold">Resilient Nodes</h3>
                                <p className="text-stone-500 text-sm leading-relaxed">
                                    GhostOS runs on a distributed network of secure nodes. There is no central point of failure, no gatekeeper, and no way to freeze your sovereign interactions.
                                </p>
                            </div>
                            <div className="space-y-6">
                                <div className="w-12 h-12 bg-stone-900 dark:bg-white rounded-xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] shadow-lg">
                                    <Shield size={24} />
                                </div>
                                <h3 className="font-serif text-2xl font-bold">Verified Code</h3>
                                <p className="text-stone-500 text-sm leading-relaxed">
                                    Open-source and audited. Every line of our smart contracts on Solana and Sui is public record, verifiable by anyone, at any time.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section id="contact" className="py-32 bg-stone-50 dark:bg-[#0A0B0F] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nobel-gold/5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
                    
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="mb-16">
                                <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#0A0B0F] dark:text-white mb-6">
                                    Get in <span className="italic text-stone-500">Touch.</span>
                                </h2>
                                <p className="text-xl text-stone-600 dark:text-stone-400 leading-relaxed font-light">
                                    Ready to integrate? Our team is available for enterprise consultations.
                                </p>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-8 justify-center max-w-3xl mx-auto">
                                <a href="mailto:ops@ghostpay.fun" className="p-8 rounded-3xl bg-white dark:bg-[#111318] border border-stone-200 dark:border-white/10 shadow-xl flex flex-col items-center gap-6 group hover:border-nobel-gold/50 transition-all duration-300 hover:-translate-y-1">
                                    <div className="w-16 h-16 rounded-2xl bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-900 dark:text-white group-hover:bg-nobel-gold group-hover:text-black transition-colors">
                                        <Mail size={28} />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">Encrypted Email</div>
                                        <div className="font-mono text-lg text-[#0A0B0F] dark:text-white">ops@ghostpay.fun</div>
                                    </div>
                                </a>

                                <div className="p-8 rounded-3xl bg-white dark:bg-[#111318] border border-stone-200 dark:border-white/10 shadow-xl flex flex-col items-center gap-6 group hover:border-nobel-gold/50 transition-all duration-300">
                                    <div className="w-16 h-16 rounded-2xl bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-900 dark:text-white group-hover:bg-nobel-gold group-hover:text-black transition-colors">
                                        <Globe size={28} />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">Network Status</div>
                                        <div className="font-mono text-lg text-[#0A0B0F] dark:text-white">Distributed Nodes</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={(p) => onNavigate(p as any)} />
        </div>
    );
};
